import type { Batch } from './Batch';

/**
 * Enum representing the status of a batch operation
 */
export enum BatchStatus {
  All = 'All',
  Queued = 'Queued',
  Processing = 'Processing',
  Completed = 'Completed',
  Failed = 'Failed',
  PartiallyCompleted = 'PartiallyCompleted',
  Cancelled = 'Cancelled',
}

/**
 * Enum representing the type of batch operation
 */
export enum BatchOperation {
  All = 'All',
  Classification = 'Classification',
  FolderExpansion = 'FolderExpansion',
  Delete = 'Delete',
}

/**
 * Enum representing the status of a file job within a batch.
 * Values match the backend `FileJob.status` / `statusFilter` contract.
 */
export enum FileJobStatus {
  All = 'All',
  Pending = 'Pending',
  Processing = 'Processing',
  Success = 'Success',
  Failed = 'Failed',
  Canceled = 'Canceled',
}

/**
 * Interface representing the payload for fetching batches
 */
export interface GetBatchesPayload {
  offset: number;
  count: number;
  status?: BatchStatus;
  operation?: BatchOperation;
  fromDate?: number;
  toDate?: number;
  orderColumn?: string;
  orderAsc?: boolean;
}

/**
 * Interface representing the response for fetching batches
 */
export interface GetBatchesResponse {
  batches: Batch[];
  total: number;
}

/**
 * Interface representing batch statistics
 */
export interface BatchStats {
  totalBatches: number;
  runningCount: number;
  queuedCount: number;
  completedCount: number;
  failedCount: number;
  partiallyCompletedCount: number;
  cancelledCount: number;
  averageProcessingTime?: number;
}

/**
 * File job within a batch (real REST shape).
 * Also used as the failed-files preview entries on batch detail.
 */
export interface FileJob {
  fileId: number;
  fileName: string;
  isFolder: boolean;
  status: FileJobStatus | string;
  lastError: string;
  updatedAt: number;
  /** 1 on first enqueue; increments on each retry. */
  attempt: number;
}

/** @deprecated Prefer FileJob — same shape as failed-list preview entries. */
export type FailedFile = FileJob;

/**
 * Interface representing detailed information about a batch
 */
export interface BatchDetail extends Batch {
  additionalData?: string;
  failedFilesList?: FileJob[];
  /** True when failedFilesList is capped (first 50); full list via getBatchFiles. */
  failedFilesListTruncated?: boolean;
}

/**
 * Payload for POST /api/batch/{batchId}/files.
 * Spec §2.3: filter field is `statusFilter` (not `status`).
 */
export interface GetBatchFilesPayload {
  offset: number;
  count: number;
  statusFilter?: FileJobStatus;
  orderColumn?: string;
  orderAsc?: boolean;
}

/**
 * Response for POST /api/batch/{batchId}/files.
 * Live BE often omits some `*Count` fields (e.g. `pendingCount`); `total` is filtered page size.
 * Present `*Count` values are batch-level (pre-filter) when returned.
 */
export interface GetBatchFilesResponse {
  batchId?: number;
  files: FileJob[];
  /** Jobs matching the current statusFilter (pagination total). */
  total: number;
  totalCount?: number;
  pendingCount?: number;
  processingCount?: number;
  successCount?: number;
  failedCount?: number;
  canceledCount?: number;
}

/**
 * Interface representing the payload for cancelling file jobs within a batch
 */
export interface CancelFileJobsPayload {
  batchId: number;
  fileIds: number[];
}

/**
 * Interface representing the response for cancelling file jobs
 */
export interface CancelFileJobsResponse {
  cancelledCount: number;
  batchStatus: string;
}

/**
 * Interface representing the payload for retrying file jobs within a batch
 */
export interface RetryFileJobsPayload {
  batchId: number;
  fileIds: number[];
}

/**
 * Interface representing the response for retrying file jobs
 */
export interface RetryFileJobsResponse {
  retriedCount: number;
  batchStatus: string;
}

/**
 * Interface representing the response for cancelling a single batch
 */
export interface CancelBatchResponse {
  batchId: number;
  filesReset: number;
  fileJobsCancelled: number;
}

/**
 * Interface representing the response for cancelling all failed batches
 */
export interface CancelAllFailedBatchesResponse {
  cancelledCount: number;
}
